import React, { Suspense } from "react";
import { navigate } from "gatsby";
import Icon from "@icon-park/react/es/all";
import { Text, theme } from "@ifgengineering/component-library";
import ReactMarkdown from "react-markdown";
import { List } from "react-content-loader";
import {
  EisFundPageContainer,
  EisFundHeaderWrapper,
  EisFundHeader,
  EisFundHeaderContent,
  EisFundHeaderTextContainer,
  BackButton,
  EisFundContentWrapper,
  EisFundContent,
  StartupLogo,
} from "./styled";
import {
  StartupDetailMainContent,
  StartupDetailMain,
  StartupDetailAside,
  StartupDetailFullInfo,
  StartupDetailVideo,
  StartupDetailLogoContainer,
  StartupDetailDataCard,
  StartupDetailSocialsContainer,
  StartupDetailSocialButton,
} from "./EisFundStartupDetail.styled";
import { EisFundStartup } from "./types";
import { TAX_BENEFIT_LABELS } from "./constants";
import EisFundInvestmentStatus from "./EisFundInvestmentStatus";
import { EntityBadge } from "@components/EntityBadge";
import { Entity } from "@ifgengineering/client-invest-sdk";
import useAnalytics from "@helpers/useAnalytics";

import noLogoImage from "@assets/cur8/img/no-logo-deal.png";

interface EisFundStartupDetailProps {
  startup: EisFundStartup;
  onBack?: () => void;
}

const formatCurrency = (amount: number, currency = "GBP"): string => {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const EisFundStartupDetail: React.FC<EisFundStartupDetailProps> = ({
  startup,
  onBack,
}) => {
  const details = startup.dealDetails;
  const firePageViewAnalytics = useAnalytics("pageView");

  React.useEffect(() => {
    const firePageView = async () => {
      (await firePageViewAnalytics)({
        eventName: "cur8:eisFundStartupDetails_pageViewed",
        startup_name: startup.name,
        startup_id: startup.id,
        account_type: startup.accountType,
      });
    };

    firePageView();
  }, [startup.id]);

  const handleBack = () => (onBack ? onBack() : navigate(-1));

  const handleExternalLinkClick = () => {
    if (startup.externalUrl) {
      window.open(startup.externalUrl, "_blank");
    }
  };

  const renderHeader = () => {
    return (
      <StartupDetailLogoContainer data-testid="startup-header">
        <StartupLogo
          src={startup.logoUrl || noLogoImage}
          alt={`${startup.name} logo`}
          onError={(e) => {
            e.currentTarget.src = noLogoImage;
          }}
          style={{ width: "64px", height: "64px" }}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Text type="H32" color="WHITE" fontFamily="archiasemibold">
              {startup.name}
            </Text>
            {startup.externalUrl && (
              <button
                onClick={handleExternalLinkClick}
                aria-label={`Visit ${startup.name} website`}
                data-testid="startup-external-link"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Icon type="Share" fill={theme.colors.BLUE600} size={20} />
              </button>
            )}
          </div>
          {startup.statusLabel && (
            <EisFundInvestmentStatus statusLabel={startup.statusLabel} />
          )}
        </div>
      </StartupDetailLogoContainer>
    );
  };

  const renderDataCards = () => {
    const isIfIsa = startup.accountType === "IF-ISA";
    const entity = startup.entity
      ? ({
          id: startup.entity.id,
          name: startup.entity.name,
          status: startup.entity.status,
        } as Entity)
      : null;

    return (
      <>
        <StartupDetailDataCard data-testid="tax-benefit-card">
          <Text type="U16" color="SLATE700">
            Tax Benefit
          </Text>
          <Text fontFamily="archiaregular" type="T40" color="SLATE900">
            {TAX_BENEFIT_LABELS[startup.taxBenefit]}
          </Text>
        </StartupDetailDataCard>

        <StartupDetailDataCard data-testid="currency-card">
          <Text type="U16" color="SLATE700">
            Currency
          </Text>
          <Text fontFamily="archiaregular" type="T40" color="SLATE900">
            {startup.currency || "GBP"}
          </Text>
        </StartupDetailDataCard>

        <StartupDetailDataCard data-testid="amount-invested-card">
          <Text type="U16" color="SLATE700">
            Amount Invested
          </Text>
          <Text fontFamily="archiaregular" type="T40" color="SLATE900">
            {formatCurrency(startup.amountInvested, startup.currency)}
          </Text>
        </StartupDetailDataCard>

        <StartupDetailDataCard data-testid="investment-account-card">
          <Text type="U16" color="SLATE700">
            Investment Account
          </Text>
          <EntityBadge
            entity={entity}
            isIfIsa={isIfIsa}
            hideInfo={true}
            iconConfig={{
              showLabel: true,
              labelSize: "H20",
              size: 24,
            }}
            displayNameConfig={{ hide: true }}
          />
        </StartupDetailDataCard>
      </>
    );
  };

  const renderSocialLinks = () => {
    const hasSocials =
      startup.externalUrl || details?.twitter || details?.companiesHouseUrl;

    if (!hasSocials) return null;

    return (
      <StartupDetailSocialsContainer data-testid="social-links">
        {startup.externalUrl && (
          <a
            href={startup.externalUrl}
            target="_blank"
            rel="noreferrer"
            aria-label={`Visit ${startup.name} website`}
          >
            <StartupDetailSocialButton>
              <Text type="S14" color="SLATE700">
                Visit Website
              </Text>
            </StartupDetailSocialButton>
          </a>
        )}
        {details?.twitter && (
          <a
            href={details.twitter}
            target="_blank"
            rel="noreferrer"
            aria-label={`${startup.name} Twitter profile`}
          >
            <StartupDetailSocialButton>
              <Icon
                type="twitter"
                theme="filled"
                fill={theme.colors.SLATE700}
                size={22}
              />
            </StartupDetailSocialButton>
          </a>
        )}
        {details?.companiesHouseUrl && (
          <a
            href={details.companiesHouseUrl}
            target="_blank"
            rel="noreferrer"
            aria-label={`${startup.name} Companies House details`}
          >
            <StartupDetailSocialButton>
              <Text type="S14" color="SLATE700">
                Company Details
              </Text>
            </StartupDetailSocialButton>
          </a>
        )}
      </StartupDetailSocialsContainer>
    );
  };

  return (
    <EisFundPageContainer data-testid="eis-fund-startup-detail">
      <EisFundHeaderWrapper>
        <EisFundHeader>
          <EisFundHeaderContent>
            <BackButton onClick={handleBack} data-testid="eis-back-button">
              <Icon
                type="LeftSmall"
                theme="filled"
                fill={theme.colors.WHITE}
                size={24}
              />
            </BackButton>
            <EisFundHeaderTextContainer>
              {renderHeader()}
            </EisFundHeaderTextContainer>
          </EisFundHeaderContent>
        </EisFundHeader>
      </EisFundHeaderWrapper>

      <EisFundContentWrapper>
        <EisFundContent>
          <StartupDetailMainContent>
            <StartupDetailMain>
              <StartupDetailFullInfo>
                {renderSocialLinks()}

                {details?.description && (
                  <Text type="H24" color="SLATE900">
                    {details.description}
                  </Text>
                )}

                {details?.videoUrl && (
                  <StartupDetailVideo>
                    <iframe src={details.videoUrl} title={startup.name} />
                  </StartupDetailVideo>
                )}

                {details?.summary && (
                  <Text as="div" type="P18" color="SLATE700">
                    {/* @ts-expect-error List component type mismatch */}
                    <Suspense fallback={<List />}>
                      <ReactMarkdown className="reactMarkdown">
                        {details.summary}
                      </ReactMarkdown>
                    </Suspense>
                  </Text>
                )}
              </StartupDetailFullInfo>
            </StartupDetailMain>
            <StartupDetailAside>{renderDataCards()}</StartupDetailAside>
          </StartupDetailMainContent>
        </EisFundContent>
      </EisFundContentWrapper>
    </EisFundPageContainer>
  );
};

export default EisFundStartupDetail;
